import type { SupabaseClient } from "@supabase/supabase-js";

import {
  checkRecoveryAccessToken,
  maskEmail,
  type RecoveryLink,
  type RecoveryTokenRefusal,
} from "./recovery-credential";

type AuthClient = Pick<SupabaseClient, "auth">;

export function isPasswordRecoveryUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const hash = new URLSearchParams(parsed.hash.replace(/^#/, ""));
    return parsed.searchParams.get("type") === "recovery" || hash.get("type") === "recovery";
  } catch {
    return false;
  }
}

/** A recovery credential that was checked and is HELD: it becomes the session only when the person submits the new password. */
export type HeldRecovery = Readonly<{
  accessToken: string;
  refreshToken: string;
  userId: string;
  /** `j***@example.com`: whose account the form is for. */
  maskedEmail: string;
}>;

export type AcquireOutcome =
  | Readonly<{ ok: true; held: HeldRecovery }>
  | Readonly<{ ok: false; reason: RecoveryTokenRefusal | "SERVER_REFUSED" | "NOT_CONFIGURED" }>;

/**
 * Takes the credential of a recovery link (CHT-1628 review, DEBT-01) and checks it. For fragment tokens the token itself; for a
 * `token_hash` the one-time hash is verified by the server on a client with NO storage (`ephemeral`), so the session of this browser is
 * not touched. Then the access token is read for its subject, age and one-time-link method, and the server confirms it (`getUser`). Nothing
 * becomes the session here.
 */
export async function acquireRecoveryCredential(
  client: AuthClient,
  ephemeral: () => AuthClient | null,
  link: Exclude<RecoveryLink, { kind: "none" }>,
  nowSeconds: number,
): Promise<AcquireOutcome> {
  let accessToken: string;
  let refreshToken: string;
  if (link.kind === "tokens") {
    accessToken = link.accessToken;
    refreshToken = link.refreshToken;
  } else {
    const spare = ephemeral();
    if (spare === null) return { ok: false, reason: "NOT_CONFIGURED" };
    const { data, error } = await spare.auth.verifyOtp({
      token_hash: link.tokenHash,
      type: "recovery",
    });
    if (error || !data.session) return { ok: false, reason: "SERVER_REFUSED" };
    accessToken = data.session.access_token;
    refreshToken = data.session.refresh_token;
  }
  const checked = checkRecoveryAccessToken(accessToken, nowSeconds);
  if (!checked.ok) return { ok: false, reason: checked.reason };
  const { data: userData, error: userError } = await client.auth.getUser(accessToken);
  if (userError || userData.user === null || userData.user.id !== checked.subject) {
    return { ok: false, reason: "SERVER_REFUSED" };
  }
  return {
    ok: true,
    held: {
      accessToken,
      refreshToken,
      userId: checked.subject,
      maskedEmail: maskEmail(userData.user.email),
    },
  };
}

/**
 * Makes the held credential the session, at submit. A session of ANOTHER account in this browser is signed out first (this browser only:
 * the other sessions of that account stay). `false` when the server refuses the tokens; the caller must stop, and must NOT fall back to
 * any session that was there before.
 */
export async function adoptHeldRecovery(client: AuthClient, held: HeldRecovery): Promise<boolean> {
  const { data: current } = await client.auth.getSession();
  const currentUser = current.session?.user?.id;
  if (currentUser !== undefined && currentUser !== held.userId) {
    await client.auth.signOut({ scope: "local" }).catch(() => undefined);
  }
  const { data, error } = await client.auth.setSession({
    access_token: held.accessToken,
    refresh_token: held.refreshToken,
  });
  return !error && data.session !== null;
}

/**
 * The shortest password the admin recovery form accepts. It is the rule of the customer app (`apps/mobile/app/auth/recover.tsx`), and it
 * is above the Supabase default (6), so the project's own policy is never the weaker one. A stricter project policy answers
 * `weak_password`, which `describeUpdateFailure` turns into fixed copy.
 */
export const MIN_RECOVERY_PASSWORD_LENGTH = 8;

// auth-js reports PASSWORD_RECOVERY from a timer after getSession() resolves, so the callback page
// cannot rely on the event alone. The browser that requests the reset leaves a one-shot marker; PKCE
// already requires the link to be opened in that same browser.
const RECOVERY_REQUESTED_KEY = "lafayette.admin.password-recovery-requested";
const RECOVERY_REQUESTED_MAX_AGE_MS = 60 * 60 * 1000; // matches the emailed link's lifetime

type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function markRecoveryRequested(storage: MarkerStorage, now = Date.now()): void {
  try {
    storage.setItem(RECOVERY_REQUESTED_KEY, String(now));
  } catch {
    // Storage may be unavailable; the PASSWORD_RECOVERY event remains as a fallback signal.
  }
}

export function consumeRecoveryRequested(storage: MarkerStorage, now = Date.now()): boolean {
  try {
    const stored = storage.getItem(RECOVERY_REQUESTED_KEY);
    if (stored === null) return false;
    storage.removeItem(RECOVERY_REQUESTED_KEY);
    const requestedAt = Number(stored);
    return Number.isFinite(requestedAt) && now - requestedAt <= RECOVERY_REQUESTED_MAX_AGE_MS;
  } catch {
    return false;
  }
}

const REQUEST_NEW_LINK =
  "This recovery link is no longer valid. Request a new password reset email.";

// Maps provider failures to fixed copy; raw provider text is never shown, but the error code is,
// so an unexpected failure can be diagnosed from a screenshot.
function describeUpdateFailure(error: { name?: string; code?: string | undefined }): string {
  if (error.name === "AuthSessionMissingError" || error.code === "session_not_found") {
    return REQUEST_NEW_LINK;
  }
  if (error.code === "same_password") {
    return "Choose a password different from the current one.";
  }
  if (error.name === "AuthWeakPasswordError" || error.code === "weak_password") {
    return "That password is too weak. Choose a longer or less common password.";
  }
  const reference = error.code ?? error.name;
  return reference
    ? `The new password could not be saved (${reference}).`
    : "The new password could not be saved.";
}

export async function updatePasswordAndSignOut(
  client: AuthClient,
  password: string,
): Promise<{ success: boolean; message?: string }> {
  const { error } = await client.auth.updateUser({ password });
  if (error) return { success: false, message: describeUpdateFailure(error) };

  // Global on purpose (CHT-1956): the staff sign-out and the recovery sign-out end every session of the account. The customer
  // app's "Sign out" is local; this one is not.
  const { error: signOutError } = await client.auth.signOut({ scope: "global" });
  return signOutError
    ? { success: false, message: "The recovery session could not be closed securely." }
    : { success: true };
}
